import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, TrendingUp } from 'lucide-react';

const MenteesSection = ({
  mentees,
  filteredMentees,
  enrolledMentees,
  notEnrolledUsers,
  atRiskMentees,
  menteeFilter,
  setMenteeFilter,
  searchTerm,
  setSearchTerm,
  handleViewMenteeProfile,
  handleMessageMentee,
  handleOpenPointsModal
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mentees</h1>
          <p className="text-gray-500">{mentees.length} total mentees across all tracks</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search by name or email..." 
              className="pl-10 pr-4 py-2 border rounded-lg w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Quick filter tabs */}
      <div className="flex gap-2 flex-wrap">
        <button 
          onClick={() => setMenteeFilter('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium ${
            menteeFilter === 'all' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          All ({mentees.length})
        </button>
        <button 
          onClick={() => setMenteeFilter('enrolled')}
          className={`px-4 py-2 rounded-full text-sm font-medium ${
            menteeFilter === 'enrolled' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          Enrolled ({enrolledMentees.length})
        </button>
        <button 
          onClick={() => setMenteeFilter('at-risk')}
          className={`px-4 py-2 rounded-full text-sm font-medium ${
            menteeFilter === 'at-risk' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          At Risk ({atRiskMentees.length})
        </button>
        <button 
          onClick={() => setMenteeFilter('inactive')}
          className={`px-4 py-2 rounded-full text-sm font-medium ${
            menteeFilter === 'inactive' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          Inactive 14+ days
        </button>
        <button 
          onClick={() => setMenteeFilter('not-enrolled')}
          className={`px-4 py-2 rounded-full text-sm font-medium ${
            menteeFilter === 'not-enrolled' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          Not Enrolled ({notEnrolledUsers.length})
        </button>
      </div>

      {/* Mentees grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
        {filteredMentees.map((mentee) => {
          const isEnrolled = mentee.enrolled_mentorship_track || mentee.membership_tier === 'mentorship' || mentee.has_paid;
          const isAtRisk = atRiskMentees.some(ar => ar.id === mentee.id);
          
          return (
            <div key={mentee.id} className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    isEnrolled ? 'bg-purple-100' : 'bg-gray-100'
                  }`}>
                    <span className={`font-medium ${isEnrolled ? 'text-purple-600' : 'text-gray-500'}`}>
                      {mentee.name?.charAt(0) || mentee.email?.charAt(0) || 'M'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{mentee.name || 'Unknown'}</p>
                    <p className="text-sm text-gray-500">
                      {mentee.enrolled_mentorship_track || (isEnrolled ? 'Enrolled' : 'Not enrolled')}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  isAtRisk ? 'bg-red-100 text-red-700' :
                  isEnrolled ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {isAtRisk ? 'At Risk' : isEnrolled ? 'Enrolled' : 'Free'}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Progress</span>
                  <span className="font-medium">{mentee.progress || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full" 
                    style={{width: `${mentee.progress || 0}%`}}
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Points</span>
                  <span className="font-medium text-purple-600">{mentee.points || 0} pts</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Level</span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    mentee.mentorship_level === 'ambassador' ? 'bg-yellow-100 text-yellow-700' :
                    mentee.mentorship_level === 'burningOne' ? 'bg-orange-100 text-orange-700' :
                    mentee.mentorship_level === 'builder' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {mentee.mentorship_level === 'burningOne' ? 'Burning One' : 
                     mentee.mentorship_level?.charAt(0).toUpperCase() + mentee.mentorship_level?.slice(1) || 'Disciple'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Active</span>
                  <span>{mentee.last_active ? new Date(mentee.last_active).toLocaleDateString() : 'Never'}</span>
                </div>
                {mentee.total_paid > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Paid</span>
                    <span className="text-green-600 font-medium">£{mentee.total_paid}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <button 
                  onClick={() => handleViewMenteeProfile(mentee.id)}
                  className="flex-1 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
                >
                  View Profile
                </button>
                <button 
                  onClick={() => handleOpenPointsModal(mentee)}
                  className="px-3 py-2 border border-purple-200 text-purple-600 rounded-lg text-sm hover:bg-purple-50"
                  title="Manage Points"
                >
                  <TrendingUp className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleMessageMentee(mentee.id, mentee.name)}
                  className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                >
                  Message
                </button>
              </div>
            </div>
          );
        })}
      </div>
      
      {filteredMentees.length === 0 && (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No mentees found matching your filters</p>
        </div>
      )}
    </div>
  );
};

export default MenteesSection;
